import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { MessageCircle, PenTool, Code2, Rocket, type LucideIcon } from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import { prefersReducedMotion, isDesktopViewport } from '@/lib/motion'
import { PROCESS_STEPS, type ProcessStep } from '@/lib/process'

const STEP_ICONS: LucideIcon[] = [MessageCircle, PenTool, Code2, Rocket]
/** % across the desktop row where each of the 4 cards sits, center-aligned. */
const CARD_CENTERS = [12.5, 37.5, 62.5, 87.5]
/** Vertical scroll distance (in viewport-heights) dedicated to the desktop pinned journey. */
const VH_PER_STEP = 55

/** Mobile: real scroll distance (in viewport-heights) reserved purely for the freeze
 * phase (move-to-bottom, morph, fly-to-top) once "Lansiranje" is centered. Was 350
 * ("long, dramatic"), then 260, still left a big gap before About per the user's
 * screenshot — cut further to 130. Still a real pause, just a much shorter tail
 * of empty scroll after it. This is the knob to keep adjusting if it's still off. */
const FROZEN_ZONE_VH = 130
/** Mobile row's own top/bottom padding, in px (matches the `pt-8`/`pb-8` Tailwind
 * classes on the row below EXACTLY — if you change those classes, update this too).
 * Needed because `card4`'s measured offset bubbles up to the row (its offsetParent,
 * since the row is the nearest `position:relative` ancestor once the translated
 * content div's own `transform` intercepts the chain — see the big comment below),
 * and that offset doesn't include the row's own padding on top of it. */
const ROW_PADDING_PX = 32
/** Creature's vertical position as a % of Column A's own height (see the layout
 * comment below) at each stage of the journey. Tune these by eye once this is
 * actually visible on a phone — they're reasonable starting guesses, not measured. */
const CREATURE_LINE = {
  crawlStart: 8,
  crawlEnd: 48, // where "Lansiranje is centered" reads on the fixed line
  freezeBottom: 58, // per the user's feedback: morph was happening too far below
  // the card — now clearly within/at the card's lower half instead of past it
  freezeTop: 20, // after flying back up, per phase 3 step 3
}
/** Freeze phase (0..1 over FROZEN_ZONE_VH) sub-phase boundaries. */
const FREEZE_MOVE_END = 0.35 // 0 -> this: larva moves down to the bottom of the card
const FREEZE_MORPH_END = 0.55 // this -> prev: morphs into a butterfly, held in place
// (FREEZE_MORPH_END -> 1): flies back up to the top of the card, fading out near the end
/** Of that final flight sub-phase, how much is spent moving horizontally
 * INTO the card first (see creatureFlightX below) before rising — per the
 * user's feedback, the butterfly should fly into the card and then up, not
 * straight up beside it along Column A's fixed line. */
const FLIGHT_ENTER_END = 0.3
/** How far right, in px, the creature travels during that entry portion —
 * enough to clear Column A's own width (w-9 = 36px) plus the gap-5 (20px)
 * between the two columns, landing it visibly over the card. */
const CARD_ENTER_PX = 72
/** Per the user's feedback: the crawl (phase 1) used to reserve exactly
 * `crawlDistancePx` of real scroll — the literal distance Column B needs
 * to translate — so it felt identical to normal 1:1 scrolling, unlike the
 * freeze phase which has a huge dedicated budget (FROZEN_ZONE_VH). This
 * multiplies the RESERVED scroll room only; the actual visual translate
 * distance (appliedOffsetPx, still `crawlT * crawlDistancePx`) is
 * untouched, so it just takes more real scrolling to get through the same
 * visual crawl — i.e. it feels slower. Bumped again per "slow it down more
 * so it's moving like [the pace of] scrolling when it is stopped" —
 * roughly matches the frozen phase's %-per-vh pace now, not just slower
 * than before. */
const CRAWL_SLOWDOWN = 4

/**
 * Scroll-driven "journey" through the 4 process steps.
 *
 * Desktop: a UFO hops card to card (Geometry Dash-style bounce), unchanged
 * — classic scroll-jack, `VH_PER_STEP * 4`vh pinned section, `progress`
 * (0–1) drives hop position. Untouched by this rewrite.
 *
 * Mobile: a larva crawls down a vertical line, and once "Lansiranje" (the
 * last card) is centered in the viewport, the ENTIRE screen genuinely
 * locks — not just the card — while further scroll input drives the
 * creature through its own local animation (move to the card's bottom,
 * morph into a butterfly, fly to the card's top), then unlocks and normal
 * scrolling continues toward the next section.
 *
 * This needed the SAME technique the desktop UFO section already uses —
 * a tall reserved-height wrapper with a `position: sticky` "viewport
 * window" inside it — not a sticky trick bolted onto one card. A single
 * `position: sticky` on just the last card (an earlier attempt) can't
 * deliver a real freeze: as soon as the sticky card's own box scrolls past
 * the point where CSS makes it "stick," everything ELSE on the page (other
 * cards, the page below) keeps scrolling normally — that's "one element
 * scrolling away from the rest," exactly what this is meant to avoid.
 *
 * How the pinned mini-section works:
 *  - A tall `mobileRef` wrapper reserves real scroll distance: one
 *    viewport-height of "resting" space, `crawlDistancePx` for the crawl
 *    (phase 1, measured — see below), plus `FROZEN_ZONE_VH` for the freeze
 *    (phases 2–3). Its child is `position: sticky; top: 0; height: 100vh;
 *    overflow: hidden` — the actual pinned "viewport window," identical in
 *    principle to the desktop section's sticky inner div.
 *  - Inside that window: Column A (the vertical line + the creature) does
 *    NOT move — it's a plain flex sibling, not inside anything transformed,
 *    so it always sits at a fixed spot in the viewport. Column B (the 4
 *    cards) DOES move: during the crawl, it's shifted up via `transform:
 *    translateY()` by an amount proportional to real scroll progress,
 *    which *simulates* normal scrolling 1:1 — visually indistinguishable
 *    from actually scrolling the cards, while giving us exact control over
 *    when it stops. Once that translateY reaches its target
 *    (`crawlDistancePx`, computed once from real layout so it lands
 *    exactly when the last card's center hits the viewport's center), it
 *    HOLDS there for the rest of the pinned section's height — nothing
 *    visibly moves except the creature, a real freeze, not an illusion.
 *  - The creature's position during the freeze is NOT tied to the cards'
 *    real DOM positions — it moves along Column A's own fixed-height line
 *    using hand-tuned percentages (`CREATURE_LINE`). This trades pixel-
 *    perfect alignment with the actual card edges for something far more
 *    robust: it can't drift out of sync with unpredictable BS/EN text
 *    height, and there's nothing fragile to measure once the crawl ends.
 *  - `crawlDistancePx` (the one thing that IS measured against real
 *    layout) is read from the last card's ref once on mount/resize/
 *    language change — see the layout effect below for exactly how,
 *    including why it can't just use a straightforward `offsetTop` chain.
 *
 * `prefers-reduced-motion` gets a plain static grid instead (no pinned
 * section, no creature) — see the early return below.
 */
export function ProcessScrollJourney({ heading }: { heading: ReactNode }) {
  const { lang } = useLanguage()
  const sectionRef = useRef<HTMLDivElement>(null) // desktop pinned wrapper
  const mobileRef = useRef<HTMLDivElement>(null) // mobile pinned wrapper
  const lastCardRef = useRef<HTMLDivElement>(null) // just "Lansiranje", for crawlDistancePx
  const lastIndex = PROCESS_STEPS.length - 1

  const [progress, setProgress] = useState(0) // desktop
  const [mobileOverall, setMobileOverall] = useState(0) // mobile, 0..1 across crawl+freeze combined
  const [crawlDistancePx, setCrawlDistancePx] = useState(0)
  const [viewportH, setViewportH] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800))
  const [reduced, setReduced] = useState(true) // defaults static until measured, see effect below
  // Only one of the desktop UFO / mobile larva variants is ever visible
  // (CSS `hidden lg:block` / `lg:hidden`, matching Tailwind's `lg` = 1024px
  // breakpoint via isDesktopViewport()) but both used to stay mounted with
  // full reserved scroll-height and both ran their own scroll math on every
  // scroll event regardless of visibility — the inactive one was pure dead
  // weight (height + per-frame cost) contributing to mobile's high TBT.
  // `isDesktop` gates which variant actually mounts below, and the scroll/
  // measure effects skip work for whichever one isn't rendered.
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null)

  useLayoutEffect(() => {
    setReduced(prefersReducedMotion())
    setIsDesktop(isDesktopViewport())
    const mq = window.matchMedia('(min-width: 1024px)')
    const onChange = () => setIsDesktop(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  // crawlDistancePx: how far Column B needs to translate up so the last
  // card's center lands on the viewport's center. Measured via `offsetTop`
  // (layout position, unaffected by the `transform` we apply for the
  // crawl), NOT `getBoundingClientRect()` (which would tangle in wherever
  // the page currently happens to be scrolled to — wrong for a value we
  // compute once and reuse for the rest of the session).
  //
  // `offsetTop` bubbles up to the nearest *positioned* ancestor. Column
  // B's translated wrapper has an inline `transform`, and in every current
  // browser a transformed element also becomes an offsetParent for its own
  // descendants (same as `position: relative` would) — so `lastCardRef`'s
  // offsetTop lands relative to THAT div, not the row. That's fine: we
  // just need to separately account for the row's own top padding
  // (`ROW_PADDING_PX`, kept in sync with the `pt-8` class by comment, see
  // the constant above) since that padding sits between the row's top
  // (= the pinned window's top = the viewport's top) and Column B's
  // content start.
  useLayoutEffect(() => {
    // isDesktop === null on the very first paint (not measured yet) — wait
    // for it rather than measuring against a variant that isn't mounted.
    if (reduced || isDesktop !== false) return
    function measure() {
      const vh = window.innerHeight
      setViewportH(vh)
      const card = lastCardRef.current
      if (!card) {
        setCrawlDistancePx(0)
        return
      }
      const centerWithinColumnB = card.offsetTop + card.offsetHeight / 2
      setCrawlDistancePx(Math.max(0, ROW_PADDING_PX + centerWithinColumnB - vh / 2))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [reduced, isDesktop, lang])

  useLayoutEffect(() => {
    if (reduced || isDesktop === null) return
    let raf = 0
    const progressThrough = (el: HTMLDivElement | null) => {
      if (!el) return 0
      const rect = el.getBoundingClientRect()
      const total = rect.height - window.innerHeight
      return total <= 0 ? 0 : Math.min(1, Math.max(0, -rect.top / total))
    }
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        // Only the mounted variant's ref is ever non-null now, but stay
        // explicit about which one we're computing for either way.
        if (isDesktop) {
          setProgress(progressThrough(sectionRef.current))
        } else {
          setMobileOverall(progressThrough(mobileRef.current))
        }
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [reduced, isDesktop])

  if (reduced) {
    return (
      <div className="mx-auto max-w-container px-6 md:px-margin-desktop">
        {heading}
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((step, i) => (
            <ProcessCard key={step.id} step={step} icon={STEP_ICONS[i]} active lang={lang} />
          ))}
        </div>
      </div>
    )
  }

  // --- Desktop: UFO hop, driven by `progress` over the pinned VH_PER_STEP journey ---
  const stepPos = progress * lastIndex // continuous 0..3
  const activeIndex = Math.min(lastIndex, Math.round(stepPos))
  const lower = Math.floor(stepPos)
  const upper = Math.min(lastIndex, lower + 1)
  const frac = stepPos - lower
  const hopArc = Math.sin(frac * Math.PI) // 0 resting on a card, 1 mid-hop

  const shipX = CARD_CENTERS[lower] + (CARD_CENTERS[upper] - CARD_CENTERS[lower]) * frac
  const shipY = -hopArc * 26

  // --- Mobile: split the wrapper's single `mobileOverall` (0..1) into the
  // crawl fraction vs. the freeze fraction, using the real measured
  // crawlDistancePx against the fixed FROZEN_ZONE_VH reserved for the
  // freeze. Both fractions are derived from the same numbers used to size
  // the wrapper itself (see the JSX below), so they can't drift apart. ---
  const frozenZonePx = (FROZEN_ZONE_VH / 100) * viewportH
  // Reserve MORE scroll than the crawl's real distance needs (see
  // CRAWL_SLOWDOWN above) — this is the only thing that changes phase 1's
  // felt speed; phase 2/3 (freeze) math below is completely untouched.
  const crawlReservedPx = crawlDistancePx * CRAWL_SLOWDOWN
  const totalMobilePx = crawlReservedPx + frozenZonePx
  const crawlFrac = totalMobilePx > 0 ? crawlReservedPx / totalMobilePx : 0

  const crawlT = crawlFrac > 0 ? Math.min(1, mobileOverall / crawlFrac) : 1
  const freezeT = crawlFrac < 1 ? Math.max(0, Math.min(1, (mobileOverall - crawlFrac) / (1 - crawlFrac))) : 0

  const appliedOffsetPx = crawlT * crawlDistancePx // holds at crawlDistancePx through the whole freeze
  const mobileActiveIndex = Math.min(lastIndex, Math.round(crawlT * lastIndex))

  // Freeze sub-phases, all driven by freezeT (0..1 across FROZEN_ZONE_VH):
  const moveT = Math.min(1, freezeT / FREEZE_MOVE_END)
  const morphT = Math.max(0, Math.min(1, (freezeT - FREEZE_MOVE_END) / (FREEZE_MORPH_END - FREEZE_MOVE_END)))
  const flightT = freezeT > FREEZE_MORPH_END ? Math.min(1, (freezeT - FREEZE_MORPH_END) / (1 - FREEZE_MORPH_END)) : 0
  // Split the flight sub-phase itself into "fly into the card" (horizontal,
  // see creatureFlightX below) then "rise to the card's top" (vertical) —
  // per the user's feedback, not a single straight-up motion.
  const flightEnterT = flightT > 0 ? Math.min(1, flightT / FLIGHT_ENTER_END) : 0
  const flightRiseT = flightT > FLIGHT_ENTER_END ? Math.min(1, (flightT - FLIGHT_ENTER_END) / (1 - FLIGHT_ENTER_END)) : 0

  let creaturePct: number
  if (crawlT < 1) {
    // Gentle vertical bob layered on top of the linear crawl position —
    // per the user's feedback that it just followed scroll 1:1 with no
    // organic "inchworm" feel. Small amplitude, doesn't touch endpoints.
    const crawlBobPct = Math.sin(mobileOverall * 20) * 1.2
    creaturePct = CREATURE_LINE.crawlStart + (CREATURE_LINE.crawlEnd - CREATURE_LINE.crawlStart) * crawlT + crawlBobPct
  } else if (freezeT <= FREEZE_MORPH_END) {
    creaturePct = CREATURE_LINE.crawlEnd + (CREATURE_LINE.freezeBottom - CREATURE_LINE.crawlEnd) * moveT
  } else {
    creaturePct = CREATURE_LINE.freezeBottom + (CREATURE_LINE.freezeTop - CREATURE_LINE.freezeBottom) * flightRiseT
  }

  const FLAP_CYCLES = 3
  const larvaOpacity = 1 - morphT
  const butterflyOpacity = morphT
  const wingFlap = Math.sin((morphT + flightT) * FLAP_CYCLES * Math.PI * 2)
  const creatureScale = 0.7 + Math.min(1, crawlT) * 0.6 + Math.sin(morphT * Math.PI) * 0.2 // grows crawling, small pop through the morph
  // Per the user's feedback: the whole-icon side-to-side wiggle (X offset +
  // rotation) was wrong — it should be individual body segments waving
  // independently, handled INSIDE the Larva component itself now (see
  // larvaWavePhase below), not the icon as a rigid whole. So during the
  // crawl this stays at 0/0 — the icon travels straight down the line
  // (still with the small vertical bob above), and only its internal
  // segments animate. During the flight sub-phase, X still carries the
  // "fly into the card" motion (FLIGHT_ENTER_END/CARD_ENTER_PX above) and
  // rotation still carries the morph-spin + flight-tilt below — untouched.
  const creatureFlightX = flightEnterT * CARD_ENTER_PX
  const creatureWiggleX = crawlT < 1 ? 0 : creatureFlightX
  const creatureRotate = morphT * 720 + flightT * -20
  const creatureOpacity = flightT > 0.85 ? Math.max(0, 1 - (flightT - 0.85) / 0.15) : 1
  // Phase driving the Larva's internal per-segment wave — only animated
  // during the crawl (0 elsewhere, so the freeze-phase larva glimpse, if
  // any, renders exactly as before — untouched per the user's earlier
  // instruction not to touch phase 2/3).
  const larvaWavePhase = crawlT < 1 ? mobileOverall * 22 : 0

  // Nothing measured yet (first paint before the layout effect runs) —
  // render neither variant rather than guessing, so there's never a flash
  // of the wrong one and never both mounted at once.
  if (isDesktop === null) return <div className="mx-auto max-w-container px-6 md:px-margin-desktop">{heading}</div>

  return (
    <>
      {/* Desktop: pinned scroll-jack, UFO hops card to card. Only mounted
          when isDesktop — previously always mounted (just CSS-hidden below
          lg), reserving 4×VH_PER_STEP vh and running scroll math even on
          mobile. */}
      {isDesktop && (
      <div ref={sectionRef} className="relative" style={{ height: `${PROCESS_STEPS.length * VH_PER_STEP}vh` }}>
        <div className="sticky top-0 overflow-hidden pb-8 pt-16">
          <div className="mx-auto mb-20 w-full max-w-container px-margin-desktop">{heading}</div>
          <div className="relative mx-auto w-full max-w-container px-margin-desktop">
            <Ufo style={{ left: `${shipX}%`, transform: `translate(-50%, ${shipY}px)` }} />
            <div className="grid grid-cols-4 gap-5">
              {PROCESS_STEPS.map((step, i) => (
                <ProcessCard key={step.id} step={step} icon={STEP_ICONS[i]} active={activeIndex === i} lang={lang} />
              ))}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Mobile/tablet: real dedicated pinned mini-section, same technique
          as the desktop wrapper above (tall reserved height + `sticky`
          viewport window) — see the file-header comment for the full
          design. Heading renders BEFORE this wrapper, in ordinary page
          flow, so it scrolls away normally before the pin engages —
          unchanged "phase 1" behavior. Only mounted when !isDesktop —
          previously always mounted (just CSS-hidden above lg), reserving
          a full extra viewport-height+ of scroll space and running its own
          scroll math even on desktop. */}
      {!isDesktop && (
      <div>
        <div className="mx-auto mb-6 w-full max-w-md px-6">{heading}</div>
        <div
          ref={mobileRef}
          className="relative"
          style={{ height: `${viewportH + crawlReservedPx + frozenZonePx}px` }}
        >
          <div className="sticky top-0 h-screen overflow-hidden">
            <div className="relative mx-auto flex h-full w-full max-w-md gap-5 px-6 pt-8 pb-8">
              {/* Column A: the line + creature. A plain flex sibling, never
                  transformed, so it always sits at a fixed spot in the
                  viewport regardless of what Column B is doing. */}
              <div className="relative w-9 shrink-0">
                <div
                  className="absolute left-1/2 top-2 w-px -translate-x-1/2 bg-gradient-to-b from-white/5 via-white/15 to-white/5"
                  style={{ bottom: `${100 - CREATURE_LINE.freezeBottom}%` }}
                />
                <div
                  className="absolute left-1/2 z-10"
                  style={{
                    top: `${creaturePct}%`,
                    transform: `translate(calc(-50% + ${creatureWiggleX}px), -50%) scale(${creatureScale}) rotate(${creatureRotate}deg)`,
                    opacity: creatureOpacity,
                  }}
                >
                  <div className="relative h-7 w-7">
                    <div className="absolute inset-0" style={{ opacity: larvaOpacity }}>
                      <Larva wavePhase={larvaWavePhase} />
                    </div>
                    <div className="absolute inset-0" style={{ opacity: butterflyOpacity }}>
                      <Butterfly flap={wingFlap} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Column B: the 4 cards. Translated up during the crawl to
                  simulate scrolling 1:1; holds in place for the entire
                  freeze — this transform value is the ONLY thing that
                  determines whether the page "looks" frozen, and it's
                  driven by our own progress math, not by any per-card
                  sticky trickery. */}
              <div className="min-w-0 flex-1 overflow-visible">
                <div
                  style={{ transform: `translateY(-${appliedOffsetPx}px)` }}
                  className="flex flex-col gap-5"
                >
                  {PROCESS_STEPS.map((step, i) => (
                    <div key={step.id} ref={i === lastIndex ? lastCardRef : undefined}>
                      <ProcessCard step={step} icon={STEP_ICONS[i]} active={mobileActiveIndex === i} lang={lang} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  )
}

function ProcessCard({
  step,
  icon: Icon,
  active,
  lang,
}: {
  step: ProcessStep
  icon: LucideIcon
  active: boolean
  lang: 'bs' | 'en'
}) {
  return (
    <div
      className={`flex flex-col gap-2.5 rounded-lg border p-5 transition-all duration-slow ${
        active
          ? 'glow-ring scale-105 border-accent-fire/50 bg-accent-fire/10 opacity-100'
          : 'scale-95 border-white/10 bg-white/[0.03] opacity-50'
      }`}
    >
      <span className="font-mono text-xs font-bold text-accent-amber-light">{String(step.num).padStart(2, '0')}</span>
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors duration-slow ${
          active ? 'bg-accent-fire/20 text-accent-fire' : 'bg-white/5 text-foreground-muted'
        }`}
      >
        <Icon size={17} />
      </div>
      <h3 className="font-serif text-base font-semibold text-foreground">{lang === 'en' ? step.titleEn : step.title}</h3>
      <p className="text-sm leading-relaxed text-foreground-muted">{lang === 'en' ? step.descEn : step.desc}</p>
    </div>
  )
}

function Ufo({ style }: { style?: CSSProperties }) {
  return (
    <div
      className="pointer-events-none absolute -top-16 z-10 drop-shadow-[0_0_18px_rgba(255,122,69,0.65)]"
      style={style}
      aria-hidden="true"
    >
      <svg width="56" height="40" viewBox="0 0 56 40">
        <defs>
          <linearGradient id="ufo-body" x1="0" y1="0" x2="56" y2="40">
            <stop offset="0%" stopColor="#ff7a45" />
            <stop offset="100%" stopColor="#ffb95f" />
          </linearGradient>
        </defs>
        <ellipse cx="28" cy="16" rx="12" ry="10" fill="url(#ufo-body)" opacity="0.85" />
        <ellipse cx="28" cy="26" rx="26" ry="8" fill="url(#ufo-body)" />
        <ellipse cx="28" cy="26" rx="26" ry="8" fill="none" stroke="#fff" strokeOpacity="0.2" />
        <circle cx="14" cy="27" r="2" fill="#0a0a14" opacity="0.6" />
        <circle cx="28" cy="30" r="2" fill="#0a0a14" opacity="0.6" />
        <circle cx="42" cy="27" r="2" fill="#0a0a14" opacity="0.6" />
      </svg>
    </div>
  )
}

function Larva({ wavePhase }: { wavePhase: number }) {
  // 5 segments total per the user's request (was 4): 4 body segments that
  // each wave independently (own sine phase, offset by SEG_PHASE_STEP per
  // segment down the body — a real traveling undulation, not the whole
  // body moving together), plus a dedicated head segment that does NOT
  // translate at all (stays centered at cx=15, never leaves the line path)
  // and instead only ROTATES, clamped to ±20° by construction (sin is
  // -1..1) so it reads as "looking" side to side rather than swimming off
  // the path.
  const SEG_AMPLITUDE = 3.5
  const SEG_PHASE_STEP = 1.1
  const bodySegments = [
    { cy: 4.6, r: 4.6 },
    { cy: 10.6, r: 4.6 },
    { cy: 16.6, r: 4.6 },
    { cy: 22.6, r: 4.6 },
  ]
  const headCy = 29
  const headR = 4.8
  const headRotate = Math.sin(wavePhase + bodySegments.length * SEG_PHASE_STEP) * 20

  return (
    <svg width="28" height="28" viewBox="0 0 30 34" aria-hidden="true">
      <defs>
        <linearGradient id="larva-body" x1="0" y1="0" x2="30" y2="34">
          <stop offset="0%" stopColor="#ffb95f" />
          <stop offset="100%" stopColor="#e0983a" />
        </linearGradient>
      </defs>
      {bodySegments.map((seg, i) => {
        const offsetX = Math.sin(wavePhase + i * SEG_PHASE_STEP) * SEG_AMPLITUDE
        return <circle key={i} cx={15 + offsetX} cy={seg.cy} r={seg.r} fill="url(#larva-body)" />
      })}
      <g style={{ transform: `rotate(${headRotate}deg)`, transformOrigin: `15px ${headCy}px` }}>
        <circle cx="15" cy={headCy} r={headR} fill="url(#larva-body)" />
        <circle cx={15 - 2.3} cy={headCy + 1.5} r="0.8" fill="#0a0a14" />
        <circle cx={15 + 2.3} cy={headCy + 1.5} r="0.8" fill="#0a0a14" />
      </g>
    </svg>
  )
}

function Butterfly({ flap }: { flap: number }) {
  // `flap` swings roughly -1..1; mapped to a wing group scaleY so the
  // wings visibly rise and fall together.
  const wingScale = 0.5 + Math.abs(flap) * 0.5
  return (
    <svg width="32" height="28" viewBox="0 0 34 30" aria-hidden="true">
      <defs>
        <linearGradient id="wing-grad" x1="0" y1="0" x2="34" y2="30">
          <stop offset="0%" stopColor="#ff7a45" />
          <stop offset="100%" stopColor="#ffb95f" />
        </linearGradient>
      </defs>
      <g style={{ transform: `scaleY(${wingScale})`, transformOrigin: '17px 16px' }}>
        <ellipse cx="10" cy="12" rx="9" ry="11" fill="url(#wing-grad)" opacity="0.9" />
        <ellipse cx="24" cy="12" rx="9" ry="11" fill="url(#wing-grad)" opacity="0.9" />
        <ellipse cx="11" cy="22" rx="5" ry="6" fill="url(#wing-grad)" opacity="0.7" />
        <ellipse cx="23" cy="22" rx="5" ry="6" fill="url(#wing-grad)" opacity="0.7" />
      </g>
      <rect x="16" y="6" width="2" height="20" rx="1" fill="#0a0a14" />
    </svg>
  )
}
